import { useEffect, useMemo, useState } from "react";
import type { Table } from "@tanstack/react-table";
import { ListFilter } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import { DataTableFilterCategories } from "./data-table-filter-categories";
import { DataTableFilterOptions } from "./data-table-filter-options";

interface DataTableFilterPopoverProps<TData> {
  table: Table<TData>;
}

export function DataTableFilterPopover<TData>({ table }: DataTableFilterPopoverProps<TData>) {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filterColumns = useMemo(() => {
    return table.getAllColumns().filter((col) => {
      const canFilter = !!col.columnDef.enableColumnFilter;
      const hasGetColumnFiltersFn = !!col.columnDef.meta?.getColumnFilters;

      if (canFilter && !hasGetColumnFiltersFn) {
        console.warn(`Column ${col.id} is filterable but missing getColumnFilters meta property`);
        return false;
      }

      if (!canFilter || !hasGetColumnFiltersFn) {
        return false;
      }

      return true;
    });
  }, [table]);

  // Compute on each render - cheap operation and ensures it stays in sync with filter values
  const totalSelectedFilters = filterColumns.reduce((count, column) => {
    const filterValue = column.getFilterValue() as string[] | undefined;
    return count + (filterValue?.length ?? 0);
  }, 0);

  const activeColumn = useMemo(() => {
    return filterColumns.find((col) => col.id === activeCategory) ?? null;
  }, [filterColumns, activeCategory]);

  // Automatically select the first category if none is selected
  useEffect(() => {
    if (filterColumns.length > 0 && !activeCategory) {
      setActiveCategory(filterColumns[0].id);
    }
  }, [filterColumns, activeCategory]);

  if (filterColumns.length === 0) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9">
          <ListFilter />
          Filters
          {totalSelectedFilters > 0 && (
            <Badge variant="default" className="h-5 min-w-5 rounded-full px-1 tabular-nums">
              {totalSelectedFilters}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[480px] p-0" align="start">
        <div className="flex max-h-[400px]">
          <DataTableFilterCategories
            columns={filterColumns}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
          <DataTableFilterOptions column={activeColumn} table={table} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
